import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import {
  backToFrontFieldTypesMapping,
  DenialReasonField,
  entityToFrontFieldTypesMapping,
  FieldTypesEnum,
  WarehouseBankField,
} from 'constants/loanActionEditor';

import { selectWarehouseBankOptions } from 'store/accounting/selectors';

import { selectDenialReasonsOptions } from './denialReasons.selectors';
import { getSystem } from './system.selectors';

export const selectLoanActions = createSelector(getSystem, ({ loanActions }) => loanActions);

export const selectActiveLoanActionsTargetType = createSelector(
  getSystem,
  ({ loanActionsTargetType }) => loanActionsTargetType,
);

export const selectLoanActionCriteriaSchema = createSelector(
  getSystem,
  ({ loanActionEditor }) => loanActionEditor.criteriaSchema,
);

const selectLoanActionFields = createSelector(
  getSystem,
  ({ loanActionEditor }) => loanActionEditor.fields,
);

const selectLoanActionFieldsList = createSelector(selectLoanActionFields, fields => {
  const normalizedEnumTypes = (fields.enumTypes || []).reduce((acc, item) => {
    acc[item.name] = item;

    return acc;
  }, {});

  return Object.entries(fields.entities || {}).flatMap(([entityKey, entityData]) =>
    Object.entries(entityData).map(([fieldKey, { propertyType: fieldType, overridenName }]) => {
      const mappedType =
        entityToFrontFieldTypesMapping[fieldKey] || backToFrontFieldTypesMapping[fieldType];

      const { members, overridenNames } = normalizedEnumTypes[fieldType] || {};

      const hasEnumMembers = !mappedType && members;
      const type = hasEnumMembers ? FieldTypesEnum.SELECT : mappedType || FieldTypesEnum.STRING;
      const options = hasEnumMembers
        ? members.map(key => ({ name: overridenNames?.[key] || key, value: key }))
        : undefined;

      return { key: fieldKey, section: entityKey, type, options, fieldType, overridenName };
    }),
  );
});

const selectDenialReasonField = createSelector(selectDenialReasonsOptions, options => ({
  key: DenialReasonField.value,
  section: DenialReasonField.section,
  type: FieldTypesEnum.MULTISELECT,
  options,
}));

const selectWarehouseBankField = createSelector(selectWarehouseBankOptions, options => ({
  key: WarehouseBankField.value,
  section: WarehouseBankField.section,
  type: FieldTypesEnum.SELECT,
  options,
}));

export const selectLoanActionFieldsMap = createSelector(
  selectLoanActionFieldsList,
  selectDenialReasonField,
  selectWarehouseBankField,
  (fields, denialReasonField, warehouseBankField) =>
    fields.reduce(
      (acc, value) => {
        acc[`${value.section}.${value.key}`] = value;

        return acc;
      },
      {
        [denialReasonField.key]: denialReasonField,
        [warehouseBankField.key]: warehouseBankField,
      },
    ),
);

export const selectLoanActionFieldOptions = createSelector(selectLoanActionFieldsList, fields =>
  fields.map(({ overridenName, key, section, fieldType }) => ({
    name: overridenName || key,
    value: `${section}.${key}`,
    section,
    fieldType,
  })),
);

export const selectExtendedLoanActionFieldOptions = createSelector(
  selectLoanActionFieldOptions,
  options => options.concat(WarehouseBankField, DenialReasonField),
);

export const selectLoanActionStaticFieldOptions = createSelector(
  selectLoanActionFieldOptions,
  options => options.concat(DenialReasonField),
);

export const selectLoanActionById = memoizeOne(id =>
  createSelector(selectLoanActions, loanActions => loanActions[id]),
);

const selectIntegrationStudioFields = createSelector(
  getSystem,
  ({ loanActionEditor }) => loanActionEditor.integrationFields,
);

export const selectIntegrationStudioCollectionRegistry = createSelector(
  selectIntegrationStudioFields,
  fields => {
    const byPath = {};

    Object.entries(fields.entities || {}).forEach(([entityName, entityData]) => {
      Object.entries(entityData).forEach(([propName, propData]) => {
        const { isCollection, propertyType, scopeOptions } = propData;

        if (isCollection && propertyType?.startsWith('array<')) {
          const elementEntity = propertyType.slice('array<'.length, -1);
          const collectionPath = `${entityName}.${propName}`;

          byPath[collectionPath] = {
            collectionPath,
            collectionName: propName,
            elementEntity,
            scopeOptions: scopeOptions || [],
          };
        }
      });
    });

    return { byPath };
  },
);

export const selectIntegrationStudioFieldOptions = createSelector(
  selectIntegrationStudioFields,
  selectIntegrationStudioCollectionRegistry,
  (fields, registry) => {
    const entities = fields.entities || {};
    const collections = Object.values(registry.byPath);
    const normalizedEnumTypes = (fields.enumTypes || []).reduce((acc, item) => {
      acc[item.name] = item;

      return acc;
    }, {});

    // Element entities are emitted once PER COLLECTION below (keyed by the
    // collection's own path), so a single element entity shared by two
    // collections does not collide — skip them in the flat scalar pass.
    const elementEntityNames = new Set(collections.map(entry => entry.elementEntity));

    const scalarOptions = Object.entries(entities).flatMap(([entityName, entityData]) => {
      if (elementEntityNames.has(entityName)) {
        return [];
      }

      return Object.entries(entityData).flatMap(([fieldKey, fieldData]) => {
        const { propertyType: fieldType, overridenName, isCollection } = fieldData;

        if (isCollection) {
          return [];
        }

        const mappedType =
          entityToFrontFieldTypesMapping[fieldKey] || backToFrontFieldTypesMapping[fieldType];
        const { members } = normalizedEnumTypes[fieldType] || {};
        const type =
          !mappedType && members ? FieldTypesEnum.SELECT : mappedType || FieldTypesEnum.STRING;

        return [
          {
            name: overridenName || fieldKey,
            value: `${entityName}.${fieldKey}`,
            section: entityName,
            fieldType,
            type,
          },
        ];
      });
    });

    // One option set per collection — attribution is keyed by the collection
    // path, so two collections of the same element entity each get their own
    // scoped fields instead of one overwriting the other.
    const elementOptions = collections.flatMap(entry =>
      Object.entries(entities[entry.elementEntity] || {}).flatMap(([fieldKey, fieldData]) => {
        const { propertyType: fieldType, overridenName, isCollection } = fieldData;

        if (isCollection) {
          return [];
        }

        return [
          {
            name: overridenName || fieldKey,
            value: `${entry.collectionPath}.${fieldKey}`,
            section: entry.collectionName,
            fieldType,
            isCollectionElement: true,
            collectionPath: entry.collectionPath,
          },
        ];
      }),
    );

    return scalarOptions.concat(elementOptions);
  },
);

/**
 * Returns every RESOLVABLE field path string for the Integration Studio editor.
 *
 * - Plain scalar fields pass through as their `value`.
 * - Collection-element fields are expanded: for each alias in the collection's
 *   scopeOptions the path becomes `CollectionPath[alias].ElementField`
 *   (e.g. `Application.Borrowers[primary].FirstName`).
 * - Every `Entity.Property` path of the loan-action expression-definitions
 *   schema is also known: request-body tokens are READ bindings, so the
 *   expression vocabulary (which includes entity ids the editable-fields
 *   schema deliberately omits — ids are immutable, never writable) is the
 *   validation authority, same as the drawer's Expression source.
 *
 * The unscoped base path (`Application.Borrowers.FirstName`) is NOT emitted —
 * only the fully-scoped variants are known/valid.
 *
 * @returns {string[]}
 */
export const selectIntegrationStudioKnownFieldPaths = createSelector(
  selectIntegrationStudioFieldOptions,
  selectIntegrationStudioCollectionRegistry,
  selectLoanActionCriteriaSchema,
  (options, registry, criteriaSchema) => {
    const editableFieldPaths = options.flatMap(option => {
      if (!option.isCollectionElement) {
        return [option.value];
      }

      const collectionEntry = registry.byPath[option.collectionPath];
      const scopeOptions = collectionEntry?.scopeOptions ?? [];
      const lastDot = option.value.lastIndexOf('.');
      const elementField = option.value.substring(lastDot + 1);

      return scopeOptions.map(({ alias }) => `${option.collectionPath}[${alias}].${elementField}`);
    });

    // Array-typed properties are skipped: an unscoped collection path ({{Loan.Draws}}) is not a
    // resolvable scalar leaf — only the editable-fields branch's [alias]-scoped variants are known.
    const expressionSchemaPaths = Object.entries(criteriaSchema?.entities ?? {}).flatMap(
      ([entityName, entityFields]) =>
        Object.entries(entityFields ?? {})
          .filter(([, fieldType]) => !(_.isString(fieldType) && fieldType.startsWith('array')))
          .map(([fieldName]) => `${entityName}.${fieldName}`),
    );

    return [...new Set([...editableFieldPaths, ...expressionSchemaPaths])];
  },
);
